import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../firebase/config';
import './UserDashboard.css';
import ConfirmationModal from '../Common/ConfirmationModal';
import ClientAppointmentDetails from './ClientAppointmentDetails';
import ProfileEditModal from './ProfileEditModal'; // Import du nouveau composant
import emailService from '../../services/emailService';

const UserDashboard = () => {
  const { currentUser, userProfile, updateUserProfile } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Nouvel état pour le modal d'édition du profil
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  
  // État pour les détails du rendez-vous
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // État pour le modal de confirmation d'annulation
  const [cancelConfirmation, setCancelConfirmation] = useState({
    isOpen: false,
    appointmentId: null,
    appointmentDetails: null
  });

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        if (!currentUser) return;

        const appointmentsRef = firestore
          .collection('appointments')
          .where('userId', '==', currentUser.uid)
          .orderBy('date', 'desc');

        const snapshot = await appointmentsRef.get();
        
        const appointmentsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Si les informations du praticien ne sont pas complètes, les récupérer
        const enrichedAppointments = await Promise.all(
          appointmentsList.map(async (appointment) => {
            // Seulement si on n'a pas déjà les détails du praticien
            if (appointment.practitionerId && !appointment.practitioner) {
              try {
                const practitionerDoc = await firestore
                  .collection('practitioners')
                  .doc(appointment.practitionerId)
                  .get();
                
                if (practitionerDoc.exists) {
                  return {
                    ...appointment,
                    practitioner: {
                      ...practitionerDoc.data(),
                      id: practitionerDoc.id
                    }
                  };
                }
              } catch (err) {
                console.error('Erreur lors de la récupération des détails du praticien:', err);
              }
            }
            return appointment;
          })
        );
        
        setAppointments(enrichedAppointments);
      } catch (err) {
        console.error('Erreur lors de la récupération des rendez-vous:', err);
        setError('Impossible de récupérer vos rendez-vous. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [currentUser]);

  // Ouvrir le modal d'édition du profil
  const openProfileEditModal = () => {
    setShowProfileEditModal(true);
  };

  // Fermer le modal d'édition du profil
  const closeProfileEditModal = () => {
    setShowProfileEditModal(false);
  };

  // Gérer la mise à jour du profil utilisateur
  const handleProfileUpdate = (updatedProfile) => {
    // Si la fonction updateUserProfile est disponible dans le contexte Auth, l'utiliser
    if (updateUserProfile) {
      updateUserProfile(updatedProfile);
    }
  };

  // Ouvrir le modal de détails du rendez-vous
  const openAppointmentDetails = (appointment) => {
    setSelectedAppointment(appointment);
  };

  // Fermer le modal de détails
  const closeAppointmentDetails = () => {
    setSelectedAppointment(null);
  };

  // Modifier pour afficher le modal de confirmation
  const handleCancelRequest = (appointment) => {
    setCancelConfirmation({
      isOpen: true,
      appointmentId: appointment.id,
      appointmentDetails: appointment
    });
    
    // Fermer le modal de détails quand on ouvre la confirmation d'annulation
    closeAppointmentDetails();
  };

  // Effectue l'annulation après confirmation
  const handleCancelAppointment = async () => {
    const { appointmentId, appointmentDetails } = cancelConfirmation;
    
    if (!appointmentId) return;
  
    try {
      const updateData = {
        status: 'canceled',
        updatedAt: new Date(),
        canceledBy: 'client'
      };
  
      await firestore.collection('appointments').doc(appointmentId).update(updateData);
  
      // Préparer les données du rendez-vous pour l'email
      const updatedAppointment = {
        ...appointmentDetails,
        ...updateData,
        id: appointmentId
      };
  
      // Envoyer un email de confirmation d'annulation au client
      try {
        emailService.sendCancellationEmail(
          updatedAppointment,
          'client',
          'client'
        );
        console.log('Email d\'annulation envoyé au client');
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi de l\'email au client:', emailError);
        // Ne pas bloquer le processus si l'envoi d'email échoue
      }
  
      // Envoyer un email de notification au praticien
      try {
        emailService.sendCancellationEmail(
          updatedAppointment,
          'practitioner',
          'client'
        );
        console.log('Email d\'annulation envoyé au praticien');
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi de l\'email au praticien:', emailError);
        // Ne pas bloquer le processus si l'envoi d'email échoue
      }
  
      // Mettre à jour la liste locale
      setAppointments(prevAppointments => 
        prevAppointments.map(appointment => 
          appointment.id === appointmentId 
            ? { ...appointment, status: 'canceled', canceledBy: 'client' } 
            : appointment
        )
      );
      
      // Fermer le modal
      closeCancelConfirmation();
      
    } catch (err) {
      console.error('Erreur lors de l\'annulation du rendez-vous:', err);
      alert('Une erreur est survenue lors de l\'annulation. Veuillez réessayer.');
    }
  };

  // Fermer le modal sans annuler
  const closeCancelConfirmation = () => {
    setCancelConfirmation({
      isOpen: false,
      appointmentId: null,
      appointmentDetails: null
    });
  };

  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Formatage de la durée pour l'affichage
  const formatDuration = (duration) => {
    if (duration === 90) return '1h30';
    if (duration === 60) return '1h';
    if (duration === 45) return '45min';
    return `${duration} min`;
  };

  // Générer le message de confirmation d'annulation
  const generateCancellationMessage = () => {
    const appointment = cancelConfirmation.appointmentDetails;
    if (!appointment) return null;
    
    return (
      <>
        <p>Vous êtes sur le point d'annuler le rendez-vous suivant :</p>
        
        <div className="appointment-summary">
          <p><strong>Praticien :</strong> {appointment.practitionerName}</p>
          <p><strong>Service :</strong> {appointment.serviceTitle}</p>
          <p><strong>Date :</strong> {appointment.formattedDate}</p>
          <p><strong>Heure :</strong> {appointment.time}</p>
        </div>
        
        <div className="policy-section">
          <div className="policy-title">Politique d'annulation</div>
          <p>
            Votre rendez-vous étant prévu dans plus de 24 heures, vous pouvez l'annuler sans frais.
          </p>
          {appointment.practitioner?.cancellationPolicy && (
            <p>{appointment.practitioner.cancellationPolicy}</p>
          )}
        </div>
        
        <p>Souhaitez-vous vraiment annuler ce rendez-vous ?</p>
      </>
    );
  };

  // Filtrer les rendez-vous selon l'onglet actif
  const filteredAppointments = appointments.filter(appointment => {
    const appointmentDate = new Date(appointment.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (activeTab === 'upcoming') {
      return appointmentDate >= today && appointment.status === 'confirmed';
    } else if (activeTab === 'past') {
      return appointmentDate < today && appointment.status === 'confirmed';
    } else if (activeTab === 'canceled') {
      return appointment.status === 'canceled';
    }
    return true;
  });

  return (
    <div className="user-dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1>Mon espace personnel</h1>
          <Link to="/booking" className="btn btn-primary">
            Nouveau rendez-vous
          </Link>
        </div>
        
        <div className="dashboard-content">
          <div className="profile-section">
            <h2>Mes informations</h2>
            <div className="profile-card">
              <div className="profile-item">
                <span className="profile-label">Nom :</span>
                <span className="profile-value">{userProfile?.name}</span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Prénom :</span>
                <span className="profile-value">{userProfile?.firstname}</span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Email :</span>
                <span className="profile-value">{currentUser?.email}</span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Téléphone :</span>
                <span className="profile-value">{userProfile?.phone}</span>
              </div>
              <button className="btn btn-outline" onClick={openProfileEditModal}>
                Modifier mes informations
              </button>
            </div>
          </div>
          
          <div className="appointments-section">
            <h2>Mes rendez-vous</h2>
            
            {loading ? (
              <div className="loading">Chargement de vos rendez-vous...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : appointments.length === 0 ? (
              <div className="no-appointments">
                <p>Vous n'avez aucun rendez-vous programmé.</p>
                <Link to="/booking" className="btn btn-secondary">
                  Prendre votre premier rendez-vous
                </Link>
              </div>
            ) : (
              <div className="appointments-list">
                <div className="appointments-tabs">
                  <button 
                    className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setActiveTab('upcoming')}
                  >
                    À venir
                  </button>
                  <button 
                    className={`tab ${activeTab === 'past' ? 'active' : ''}`}
                    onClick={() => setActiveTab('past')}
                  >
                    Passés
                  </button>
                  <button 
                    className={`tab ${activeTab === 'canceled' ? 'active' : ''}`}
                    onClick={() => setActiveTab('canceled')}
                  >
                    Annulés
                  </button>
                </div>
                
                <div className="appointments-cards">
                  {filteredAppointments.length === 0 ? (
                    <div className="no-appointments">
                      <p>Aucun rendez-vous dans cette catégorie.</p>
                    </div>
                  ) : (
                    filteredAppointments.map(appointment => (
                      <div 
                        key={appointment.id} 
                        className="appointment-card"
                        onClick={() => openAppointmentDetails(appointment)}
                      >
                        <div className="appointment-date">
                          {formatDate(appointment.date)}
                        </div>
                        <div className="appointment-time">
                          <i className="icon-clock"></i> {appointment.time}
                        </div>
                        <div className="appointment-service">
                          <span className="service-title">{appointment.serviceTitle}</span>
                          <span className="service-duration">
                            {formatDuration(appointment.duration)}
                          </span>
                          <span className="service-price">{appointment.price}€</span>
                          {appointment.isRemote && (
                            <span className="client-remote-badge">À distance</span>
                          )}
                        </div>
                        <div className="appointment-practitioner">
                          <span className="practitioner-name">{appointment.practitionerName}</span>
                        </div>
                        {activeTab === 'canceled' && appointment.canceledBy && (
                          <div className="cancellation-info">
                            Annulé par: {appointment.canceledBy === 'client' ? 'Vous' : 'Le praticien'}
                          </div>
                        )}
                        <div className="view-details-hint">Cliquez pour voir les détails</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de confirmation d'annulation */}
      <ConfirmationModal
        isOpen={cancelConfirmation.isOpen}
        onClose={closeCancelConfirmation}
        onConfirm={handleCancelAppointment}
        title="Confirmation d'annulation"
        message={generateCancellationMessage()}
        cancelText="Revenir"
        confirmText="Confirmer l'annulation"
      />
      
      {/* Modal de détails du rendez-vous */}
      {selectedAppointment && (
        <ClientAppointmentDetails 
          appointment={selectedAppointment}
          onClose={closeAppointmentDetails}
          onCancelRequest={handleCancelRequest}
        />
      )}
      
      {/* Modal d'édition du profil */}
      {showProfileEditModal && (
        <ProfileEditModal 
          user={currentUser}
          userProfile={userProfile}
          onClose={closeProfileEditModal}
          onUpdate={handleProfileUpdate}
        />
      )}
    </div>
  );
};

export default UserDashboard;